/*
 * The activity feed behind the bell (like Jobber's): what happened across the company, newest first, one line per thing.
 * Each row opens the record it is about. Each login picks which kinds it sees (kept on that device).
 */
import { z } from "zod";
import type { Action } from "./permissions.js";

export const FEED_KINDS = [
  "clients",
  "requests",
  "quotes",
  "jobs",
  "visits",
  "invoices",
  "payments",
  "notes",
  "marketing",
  "timesheets",
] as const;
export type FeedKind = (typeof FEED_KINDS)[number];

export const FEED_LABELS: Record<FeedKind, string> = {
  clients: "Clients",
  requests: "Requests",
  quotes: "Quotes",
  jobs: "Jobs",
  visits: "Visits",
  invoices: "Invoices",
  payments: "Payments",
  notes: "Notes",
  marketing: "Marketing",
  timesheets: "Timesheets",
};

/** What a role must be able to read to see each kind in the feed. */
export const FEED_ACCESS: Record<FeedKind, Action> = {
  clients: "client.read",
  requests: "lead.read",
  quotes: "quote.read",
  jobs: "job.read",
  visits: "visit.read",
  invoices: "invoice.read",
  payments: "invoice.read",
  notes: "client.read",
  marketing: "marketing.read",
  timesheets: "payroll.read",
};

export const feedQuery = z.object({
  /** Comma list of kinds; left out means every kind this login may see. */
  kinds: z.string().max(200).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type FeedQuery = z.infer<typeof feedQuery>;

export const feedItem = z.object({
  id: z.string(),
  kind: z.enum(FEED_KINDS),
  at: z.string(),
  /** "Halfi Rivera approved a quote - $280.00" */
  title: z.string(),
  /** A quoted note body, when the row is a note. */
  quote: z.string().nullable(),
  /** "Quote #473 - Deep House Cleaning" */
  detail: z.string().nullable(),
  client: z.string().nullable(),
  link: z.string().nullable(),
});
export type FeedItem = z.infer<typeof feedItem>;

export const feedAnswer = z.object({ items: z.array(feedItem), next: z.string().nullable() });
export type FeedAnswer = z.infer<typeof feedAnswer>;
